import { CalendarOutlined, DollarOutlined, LockOutlined, SettingOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, ICellEditorParams, ICellRendererParams } from 'ag-grid-community'

import { DatePicker, Dropdown, Switch, Tooltip } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { useDriverManagement } from '@/api/useDriverManagement'
import { BasicSelectEditor } from '@/components/_SiteWide/Grid/CellEditors/BasicSelectEditor'
import { stringComparator } from '@/components/_SiteWide/Grid/dirtyEditUtil'
import type { components } from '@/types/api/v1'
import { formatters } from '@/utils/intl/dateTimeFormatters'
import { InCabTripModes, SupplyOptionModes } from './Form/types'
import { getSupplyOptionsModeText, getTripModeText } from './Form/util'
import { AmPmLabel } from './GridColumns/AmPmLabel'
import {
  ScheduleTemplateEditor,
  type ScheduleTemplateValue,
  scheduleTemplateColumn,
} from './GridColumns/ScheduleTemplateEditor'
import { TimeEditor } from './GridColumns/TimeEditor'
import { TemplatePreview } from './TemplateManagement/TemplatePreview'

// Extend dayjs with custom parse format
dayjs.extend(customParseFormat)

export type Row = components['schemas']['DriverInfoRow']

// Driver info specific columns
export const driverInfoColumns = (
  canEditDriverInfo: boolean,
  canChangePassword: boolean,
  onlyActiveDrivers: boolean,
  handlePasswordChange: (driver?: Row) => void,
  handlePayConfigClick: (driver?: Row) => void,
  handleTemplateSave: (driver: Row, value: ScheduleTemplateValue) => void
): Array<ColDef<Row> | undefined> => [
  {
    headerName: 'Name',
    pinned: 'left',
    field: 'name',
    sort: 'asc',
    initialSort: 'asc',
    comparator: (a?: string, b?: string) => a?.localeCompare(b ?? '') ?? 0,
    filter: 'agTextColumnFilter',
    width: 160,
    minWidth: 160,
    // editable: false,
  },
  {
    headerName: 'Username',
    field: 'username',
  },
  {
    headerName: 'Status',
    field: 'status',
    filter: 'agSetColumnFilter',
    width: 140,
    minWidth: 140,
    editable: canEditDriverInfo,
    cellRenderer: ({ data, colDef, value, setValue }: ICellRendererParams<Row, string>) => {
      const isActive = value === 'ACTIVE'
      return (
        <Tooltip title={`Click to ${isActive ? 'deactivate' : 'activate'} driver`}>
          <Horizontal gap={8} alignItems='center' justifyContent='center'>
            <Switch
              checked={isActive}
              onChange={(value) => {
                if (!data) return
                setValue?.(value ? 'ACTIVE' : 'INACTIVE')
              }}
              disabled={!colDef?.editable}
              checkedChildren='Active'
              unCheckedChildren='Inactive'
            />
          </Horizontal>
        </Tooltip>
      )
    },
  },
  {
    headerName: 'Email',
    field: 'work_email',
    filter: 'agTextColumnFilter',
    width: 200,
    minWidth: 200,
    editable: canEditDriverInfo,
  },
  {
    headerName: 'Title',
    field: 'title',
    filter: 'agTextColumnFilter',
    width: 140,
    editable: canEditDriverInfo,
    comparator: stringComparator,
  },
  {
    headerName: 'Supervisor',
    field: 'supervisor',
    filter: 'agTextColumnFilter',
    width: 150,
    minWidth: 150,
    editable: canEditDriverInfo,
  },
  {
    headerName: 'Hire Date',
    field: 'hire_date',
    filter: 'agDateColumnFilter',
    width: 130,
    minWidth: 130,
    valueFormatter: ({ value }) => formatters.DATE(value),
    valueGetter: ({ data }) => (data?.hire_date ? dayjs(data.hire_date) : null),
    cellEditor: forwardRef((params: ICellEditorParams<Row, Dayjs>, ref) => {
      const [date, setDate] = useState(params.value)
      const [hasSetValue, setHasSetValue] = useState(false)

      useEffect(() => {
        if (hasSetValue) {
          params.stopEditing()
        }
      }, [params.stopEditing, hasSetValue])

      useImperativeHandle(ref, () => ({
        isCancelAfterEnd: () => !hasSetValue,
        getValue: () => date?.toISOString() ?? null,
      }))

      return (
        <DatePicker
          format='MM-DD-YYYY'
          open
          value={date}
          onChange={(date) => {
            setDate(date)
            setHasSetValue(true)
          }}
        />
      )
    }),
  },
  {
    headerName: 'Exp. Start Date',
    headerTooltip: 'Experience Start Date',
    field: 'experience_start_date',
    filter: 'agDateColumnFilter',
    initialWidth: 150,
    minWidth: 130,
    editable: canEditDriverInfo,
    valueFormatter: ({ value }) => {
      if (!value) return ''
      return formatters.DATE(value)
    },
    cellEditorSelector: () => {
      return {
        component: 'agDateCellEditor',
        params: {
          min: '1900-01-01',
          max: dayjs().add(1, 'year').format('YYYY-MM-DD'),
        },
      }
    },
    filterParams: {
      comparator: (filterLocalDateAtMidnight, cellValue) => {
        if (!cellValue) return -1

        const cellDateStr = dayjs(cellValue).format('YYYY-MM-DD')
        const filterDateStr = dayjs(filterLocalDateAtMidnight).format('YYYY-MM-DD')

        if (cellDateStr === filterDateStr) return 0
        if (cellDateStr < filterDateStr) return -1
        return 1
      },
    },
    valueGetter: (params) => params?.data?.experience_start_date,
    valueSetter: (params) => {
      if (!params.data) return false

      params.data.experience_start_date = params.newValue
      return true
    },
  },
  !onlyActiveDrivers
    ? {
        headerName: 'Termination Date',
        field: 'termination_date',
        filter: 'agDateColumnFilter',
        width: 140,
        valueFormatter: ({ value }) => formatters.DATE(value),
        valueGetter: ({ data }) => (data?.termination_date ? dayjs(data.termination_date) : null),
        cellEditor: forwardRef((params: ICellEditorParams<Row, Dayjs>, ref) => {
          const [date, setDate] = useState(params.value)
          const [hasSetValue, setHasSetValue] = useState(false)

          useEffect(() => {
            if (hasSetValue) {
              params.stopEditing()
            }
          }, [params.stopEditing, hasSetValue])

          useImperativeHandle(ref, () => ({
            isCancelAfterEnd: () => !hasSetValue,
            getValue: () => date?.toISOString() ?? null,
          }))

          return (
            <DatePicker
              open
              format='MM-DD-YYYY'
              value={date}
              onChange={(date) => {
                setDate(date)
                setHasSetValue(true)
              }}
            />
          )
        }),
      }
    : undefined,
  {
    headerName: 'Depot',
    field: 'depot',
    filter: 'agSetColumnFilter',
    width: 150,
    editable: canEditDriverInfo,
    cellEditor: forwardRef((params: ICellEditorParams<Row, string>, ref) => {
      //todo: eventually it might be nice to create some central entity-aware select lists
      //so that we can just use something like <DepotSelect/>. for now keep as-is
      //since it is pulling from a domain-specific endpoint (/drivers/metadata)
      const { useDriverMetadata } = useDriverManagement()
      const { data, isLoading } = useDriverMetadata()
      const options = useMemo(
        () => data?.depots.map((x) => ({ value: x, label: x }))?.sort((a, b) => a.label.localeCompare(b.label)) ?? [],
        [data]
      )
      return (
        <BasicSelectEditor
          {...params}
          showSearch
          ref={ref}
          options={options}
          placeholder='Select Depot'
          loading={isLoading}
        />
      )
    }),
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      height: '100%',
    },
    cellRenderer: ({ value }: ICellRendererParams<Row, string>) => {
      if (!value) return null
      return (
        <Vertical style={{ width: '100%', height: '100%' }} gap={2} justifyContent='center'>
          <Horizontal alignItems='center' gap={4}>
            <MaterialIcon icon='location_on' style={{ color: 'var(--theme-color-2)' }} />
            <Texto>{value}</Texto>
          </Horizontal>
        </Vertical>
      )
    },
  },
  {
    headerName: 'Tractor',
    field: 'tractor_number',
    filter: 'agSetColumnFilter',
    editable: canEditDriverInfo,
    cellEditor: forwardRef((params: ICellEditorParams<Row, string>, ref) => {
      //todo: eventually it might be nice to create some central entity-aware select lists
      //so that we can just use something like <TractorSelect/>. for now keep as-is
      //since it is pulling from a domain-specific endpoint (/drivers/metadata)
      const { useDriverMetadata } = useDriverManagement()
      const { data, isLoading } = useDriverMetadata()
      const options = useMemo(
        () => data?.tractors.map((x) => ({ value: x, label: x }))?.sort((a, b) => a.label.localeCompare(b.label)) ?? [],
        [data]
      )
      return (
        <BasicSelectEditor
          {...params}
          allowClear
          showSearch
          ref={ref}
          options={options}
          placeholder='Select Tractor'
          loading={isLoading}
        />
      )
    }),
  },
  {
    headerName: 'Trailer',
    field: 'trailer_number',
    filter: 'agSetColumnFilter',
    editable: canEditDriverInfo,
    cellEditor: forwardRef((params: ICellEditorParams<Row, string>, ref) => {
      //todo: eventually it might be nice to create some central entity-aware select lists
      //so that we can just use something like <TrailorSelect/>. for now keep as-is
      //since it is pulling from a domain-specific endpoint (/drivers/metadata)
      const { useDriverMetadata } = useDriverManagement()
      const { data, isLoading } = useDriverMetadata()
      const options = useMemo(
        () => data?.trailers.map((x) => ({ value: x, label: x }))?.sort((a, b) => a.label.localeCompare(b.label)) ?? [],
        [data]
      )
      return (
        <BasicSelectEditor
          {...params}
          allowClear
          showSearch
          ref={ref}
          options={options}
          placeholder='Select Trailer'
          loading={isLoading}
        />
      )
    }),
  },
  {
    headerName: 'Pay Config',
    field: 'pay_configuration',
    filter: 'agSetColumnFilter',
    width: 160,
    editable: false,
  },
  {
    headerName: 'In-Cab Trip Mode',
    field: 'in_cab_trip_mode',
    filter: 'agSetColumnFilter',
    editable: canEditDriverInfo,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: () => ({
      values: Object.values(InCabTripModes),
      formatValue: getTripModeText,
    }),
  },
  {
    headerName: 'In-Cab Supply Options Mode',
    field: 'in_cab_supply_option_mode',
    filter: 'agSetColumnFilter',
    editable: canEditDriverInfo,
    cellEditor: 'agRichSelectCellEditor',
    cellEditorPopup: true,
    cellEditorParams: {
      values: Object.values(SupplyOptionModes),
      formatValue: getSupplyOptionsModeText,
    },
  },
  {
    headerName: 'Shift Preference',
    field: 'shift_preference',
    filter: 'agSetColumnFilter',
    cellEditorPopup: true,
    width: 140,
    minWidth: 140,
    editable: canEditDriverInfo,
    cellEditor: forwardRef((params: ICellEditorParams<Row, 'am' | 'pm' | null>, ref) => (
      <BasicSelectEditor
        {...params}
        ref={ref}
        options={[
          { value: null, label: <AmPmLabel /> },
          { value: 'am', label: <AmPmLabel value='am' /> },
          { value: 'pm', label: <AmPmLabel value='pm' /> },
        ]}
        placeholder='Select Shift Preference'
        showSearch={false}
      />
    )),
    cellRenderer: AmPmLabel,
  },
  {
    headerName: 'Shift Length',
    field: 'shift_length.shift_length_hours',
    filter: 'agNumberColumnFilter',
    width: 120,
    minWidth: 120,
    editable: canEditDriverInfo,
    cellEditor: forwardRef((params: ICellEditorParams<Row, number>, ref) => (
      <BasicSelectEditor
        {...params}
        ref={ref}
        options={Array.from({ length: 48 }, (_, i) => {
          const hours = (i + 1) * 0.5
          const label = hours % 1 === 0 ? `${hours}h` : `${Math.floor(hours)}h ${(hours % 1) * 60}m`
          return {
            value: hours,
            label: label,
          }
        })}
        placeholder='Select Shift Length'
        showSearch={false}
      />
    )),
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      height: '100%',
    },
    cellRenderer: ({ value }: ICellRendererParams<Row, number>) => {
      if (!value) return null

      return (
        <Vertical style={{ width: '100%', height: '100%' }} gap={2} justifyContent='center'>
          <Horizontal alignItems='center' gap={4}>
            <MaterialIcon icon='schedule' style={{ color: 'var(--theme-color-2)' }} />
            <Texto>{`${value} hrs`}</Texto>
          </Horizontal>
        </Vertical>
      )
    },
  },
  ShiftDefaultColumn({ field: 'am_shift.start_time', headerName: 'AM Start Default', canEditDriverInfo }),
  ShiftDefaultColumn({ field: 'pm_shift.start_time', headerName: 'PM Start Default', canEditDriverInfo }),
  {
    ...scheduleTemplateColumn,
    editable: false,
  },
  {
    headerName: 'Pattern Start Date',
    field: 'pattern_schedule_start_date',
    filter: 'agDateColumnFilter',
    valueFormatter: ({ value }) => formatters.DATE(value),
    valueGetter: ({ data }) => (data?.pattern_schedule_start_date ? dayjs(data.pattern_schedule_start_date) : null),
    editable: false,
  },
  {
    headerName: 'Template Preview',
    hide: true,
    field: 'preferred_template_days',
    filter: false,
    sortable: false,
    width: 120,
    minWidth: 120,
    editable: false,
    cellRenderer: ({ value }: ICellRendererParams<Row, Row['preferred_template_days']>) => {
      // If no template is assigned
      if (!value) {
        return (
          <Texto appearance='hint' style={{ fontSize: '12px' }}>
            No template assigned
          </Texto>
        )
      }

      return (
        <Vertical alignItems='center' justifyContent='center'>
          <TemplatePreview value={value.map((x) => x.shift)} compact maxDays={7} />
        </Vertical>
      )
    },
  },
  {
    headerName: 'Source ID',
    field: 'source_id',
    filter: 'agSetColumnFilter',
    width: 120,
    editable: canEditDriverInfo,
  },
  {
    headerName: 'Source System ID',
    field: 'source_system_id',
    filter: 'agSetColumnFilter',
    width: 120,
    editable: canEditDriverInfo,
  },
  {
    headerName: 'Actions',
    colId: 'actions',
    width: 120,
    pinned: 'right' as const,
    sortable: false,
    filter: false,
    suppressMenu: true,
    cellRenderer: (params: ICellRendererParams<Row>) => (
      <Dropdown
        trigger={['click']}
        disabled={!canEditDriverInfo && !canChangePassword}
        menu={{
          items: [
            canChangePassword
              ? {
                  key: 'password',
                  icon: <LockOutlined />,
                  label: 'Change Password',
                  onClick: () => handlePasswordChange(params.data),
                }
              : null,
            canEditDriverInfo
              ? {
                  key: 'pay',
                  icon: <DollarOutlined />,
                  label: 'Edit Pay Config',
                  onClick: () => handlePayConfigClick(params.data),
                }
              : null,
            canEditDriverInfo
              ? {
                  key: 'configure',
                  icon: <CalendarOutlined />,
                  label: <ScheduleTemplateEditor driver={params.data} onChange={handleTemplateSave} />,
                }
              : null,
          ],
        }}
      >
        <GraviButton icon={<SettingOutlined />} buttonText='Configure' size='small' />
      </Dropdown>
    ),
  },
]

type ShiftDefaultColumnParams = {
  field: ColDef<Row>['field']
  headerName: ColDef<Row>['headerName']
  canEditDriverInfo: boolean
}

function ShiftDefaultColumn({ field, headerName, canEditDriverInfo }: ShiftDefaultColumnParams): ColDef<Row> {
  const shiftType = field === 'am_shift.start_time' ? 'am_shift' : 'pm_shift'
  return {
    field,
    headerName,
    width: 140,
    minWidth: 140,
    editable: canEditDriverInfo,
    cellEditor: TimeEditor,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      height: '100%',
    },
    valueFormatter: (params) => {
      if (!params.value) return ''
      return formatters.TIME(params.value)
    },
    cellRenderer: ({ value, data }: ICellRendererParams<Row, string>) => {
      if (!value) return null
      const depotTZ = data?.[shiftType]?.timezone_data?.depot as string

      return (
        <Vertical style={{ width: '100%', height: '100%' }} gap={2} justifyContent='center'>
          <Horizontal alignItems='center' gap={4}>
            <MaterialIcon icon='schedule' style={{ color: 'var(--theme-color-2)' }} />
            <Texto>{formatters.TIME(dayjs(data?.[shiftType]?.start_time), depotTZ)}</Texto>
          </Horizontal>
        </Vertical>
      )
    },
  }
}
